'use client';

import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  speedX: number;
  speedY: number;
  twinkleSpeed: number;
  twinkleOffset: number;
  isAccent: boolean;
  blur: number;
  layer: number; // 1=far, 2=mid, 3=near
}

export default function ParticleCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    const FPS = 30;
    const FRAME_TIME = 1000 / FPS;
    let lastTime = 0;

    const CONFIG = {
      totalParticles: 90, // reduced from 120
      accentRatio: 0.08,
      layerSpeeds: [0.08, 0.18, 0.32],
      minSize: 0.4,
      maxSize: 2.2,
      minOpacity: 0.08,
      maxOpacity: 0.45,
    };

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      const scale = Math.min(window.devicePixelRatio, 1.5);
      canvas.width = width * scale;
      canvas.height = height * scale;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
    }

    function createParticle(index: number): Particle {
      const layer =
        index < CONFIG.totalParticles * 0.5
          ? 1
          : index < CONFIG.totalParticles * 0.8
          ? 2
          : 3;

      return {
        x: Math.random() * width,
        y: Math.random() * height,
        size:
          CONFIG.minSize +
          Math.random() *
            (CONFIG.maxSize - CONFIG.minSize),
        opacity:
          CONFIG.minOpacity +
          Math.random() *
            (CONFIG.maxOpacity - CONFIG.minOpacity),
        speedX:
          (Math.random() - 0.5) *
          CONFIG.layerSpeeds[layer - 1],
        speedY:
          (Math.random() - 0.5) *
            CONFIG.layerSpeeds[layer - 1] - 0.04,
        twinkleSpeed:
          0.003 + Math.random() * 0.008,
        twinkleOffset:
          Math.random() * Math.PI * 2,
        isAccent:
          Math.random() < CONFIG.accentRatio,
        blur: layer === 1 ? 1 : 0, // reduced blur
        layer,
      };
    }

    function initParticles() {
      particles =
        Array.from({ length: CONFIG.totalParticles },
          (_, i) => createParticle(i));
    }

    let time = 0;

    function draw(now: number) {
      if (now - lastTime < FRAME_TIME) {
        animationId =
          requestAnimationFrame(draw);
        return;
      }
      lastTime = now;

      ctx.clearRect(0, 0, width, height);
      time += 0.01;

      for (const p of particles) {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < -5) p.x = width + 5;
        if (p.x > width + 5) p.x = -5;
        if (p.y < -5) p.y = height + 5;
        if (p.y > height + 5) p.y = -5;

        const twinkle =
          Math.sin(time *
            p.twinkleSpeed *
            100 +
            p.twinkleOffset);
        const opacity =
          p.opacity *
          (0.6 + 0.4 * twinkle);

        ctx.beginPath();
        ctx.arc(p.x,
          p.y,
          p.size,
          0,
          Math.PI * 2);
        ctx.fillStyle =
          p.isAccent
            ? `rgba(106,92,255,${opacity})`
            : `rgba(232,232,239,${opacity})`;
        ctx.fill();
      }

      animationId =
        requestAnimationFrame(draw);
    }

    resize();
    initParticles();
    animationId =
      requestAnimationFrame(draw);

    window.addEventListener('resize',
      resize,
      { passive: true });

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      id="vanta-canvas"
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}